import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton, Alert, Switch, FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { databaseConfigsAPI, databaseTypesAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DatabaseConfig, DatabaseType } from '../../types';

interface DatabaseConfigFormData { databaseTypeId: number | string; cores: number | string; tier: string; displayOrder: number; isActive: boolean; }

const DatabaseConfigs = () => {
  const [configs, setConfigs] = useState<DatabaseConfig[]>([]);
  const [databaseTypes, setDatabaseTypes] = useState<DatabaseType[]>([]);
  const [selectedType, setSelectedType] = useState<number | string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<DatabaseConfig | null>(null);
  const [formData, setFormData] = useState<DatabaseConfigFormData>({ databaseTypeId: '', cores: '', tier: '', displayOrder: 0, isActive: true });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchDatabaseTypes(); }, []);

  const fetchConfigs = useCallback(async () => {
    if (databaseTypes.length === 0) return;
    try {
      const params = selectedType ? { databaseTypeId: selectedType } : {};
      const response = await databaseConfigsAPI.getAll(params);
      setConfigs(response.data); setError('');
    } catch (err) { setError('Failed to load database configurations'); } finally { setLoading(false); }
  }, [selectedType, databaseTypes.length]);

  useEffect(() => { if (databaseTypes.length > 0) { fetchConfigs(); } }, [selectedType, databaseTypes.length, fetchConfigs]);

  const fetchDatabaseTypes = async () => {
    try {
      const response = await databaseTypesAPI.getAll();
      const managedTypes = response.data.filter((t: DatabaseType) => { if (t.DeploymentType === 'vm_based') return false; return t.DeploymentType === 'managed' || !t.DeploymentType; });
      setDatabaseTypes(managedTypes);
    } catch (err) { setError('Failed to load database types'); }
  };

  const handleOpenDialog = (config: DatabaseConfig | null = null) => {
    if (config) { setEditingConfig(config); setFormData({ databaseTypeId: config.DatabaseTypeId, cores: config.Cores, tier: config.Tier, displayOrder: config.DisplayOrder, isActive: !!config.IsActive }); }
    else { setEditingConfig(null); setFormData({ databaseTypeId: selectedType || '', cores: '', tier: '', displayOrder: 0, isActive: true }); }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditingConfig(null); setFormData({ databaseTypeId: '', cores: '', tier: '', displayOrder: 0, isActive: true }); };

  const handleSubmit = async () => {
    try {
      if (editingConfig) { await databaseConfigsAPI.update(editingConfig.DatabaseConfigId, formData); } else { await databaseConfigsAPI.create(formData); }
      fetchConfigs(); handleCloseDialog(); setError('');
    } catch (err: unknown) { const axiosErr = err as { response?: { data?: { error?: string } } }; setError(axiosErr.response?.data?.error || 'Failed to save database configuration'); }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { await databaseConfigsAPI.delete(id); fetchConfigs(); setError(''); } catch (err) { setError('Failed to delete database configuration'); }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Database Configurations (Managed/PaaS)</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => handleOpenDialog()} disabled={databaseTypes.length === 0}>Add Configuration</Button>
      </Box>
      {databaseTypes.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No managed/PaaS database types found. VM-based databases use the "Database Editions" page instead.</Alert>}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField select label="Filter by Database Type" value={selectedType} onChange={(e) => setSelectedType(e.target.value)} sx={{ minWidth: 250 }} disabled={databaseTypes.length === 0}>
          <MenuItem value="">All Types</MenuItem>
          {databaseTypes.map((type) => (<MenuItem key={type.DatabaseTypeId} value={type.DatabaseTypeId}>{type.DatabaseName}</MenuItem>))}
        </TextField>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Database Type</TableCell><TableCell>Cores</TableCell><TableCell>Tier</TableCell><TableCell>Display Order</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
          <TableBody>
            {configs.map((config) => (
              <TableRow key={config.DatabaseConfigId}>
                <TableCell>{config.DatabaseName}</TableCell><TableCell>{config.Cores}</TableCell><TableCell>{config.Tier}</TableCell><TableCell>{config.DisplayOrder}</TableCell><TableCell>{config.IsActive ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(config)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => handleDeleteClick(config.DatabaseConfigId)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingConfig ? 'Edit Database Configuration' : 'Add Database Configuration'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>This is for Managed/PaaS databases only (Cores + Tier). For VM-based databases, use the "Database Editions" page instead.</Alert>
          <TextField select margin="dense" size="small" label="Database Type" fullWidth value={formData.databaseTypeId} onChange={(e) => setFormData({ ...formData, databaseTypeId: e.target.value })} required>
            {databaseTypes.map((type) => (<MenuItem key={type.DatabaseTypeId} value={type.DatabaseTypeId}>{type.DatabaseName}</MenuItem>))}
          </TextField>
          <TextField margin="dense" size="small" label="Cores" type="number" fullWidth value={formData.cores} onChange={(e) => setFormData({ ...formData, cores: parseInt(e.target.value) || '' })} required helperText="e.g., 2, 4, 8, 16, 32" />
          <TextField margin="dense" size="small" label="Tier" fullWidth value={formData.tier} onChange={(e) => setFormData({ ...formData, tier: e.target.value })} required helperText='"Standard", "General Purpose", "Business Critical"' />
          <TextField margin="dense" size="small" label="Display Order" type="number" fullWidth value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} />
          <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!formData.databaseTypeId || !formData.cores || !formData.tier}>{editingConfig ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Deactivate Database Configuration"
        message="Are you sure you want to deactivate this database configuration?"
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default DatabaseConfigs;
